import def from 'lodash/defaultTo';
import isEmpty from 'lodash/isEmpty';

import { ROUTES } from '@/router/baseRoutes';

import { parseBool } from '@/helpers/parseBool';

import { RefreshTokenUpdateError } from 'src/api/core/errors';

import { getDateWithoutTime } from '../helpers/dates';

import { Config } from './types';

const versioning = {
  VERSION: __VERSION__,
  RELEASE: __RELEASE__,
};

export const config: Readonly<Config> = {
  ...versioning,
  binotel: {
    hash: def(import.meta.env.VITE_BINOTEL_HASH, null),
  },
  domain: {
    frontAppBaseUrl: def(import.meta.env.VITE_API_FRONT_APP_URL?.replace('/api', ''), ''),
  },
  auth: {
    refreshTimeout: def(Number(import.meta.env.VITE_REFRESH_TIMEOUT), null),
    initialPage: ROUTES.USER.REQUESTS.ACTIVE_REQUESTS,
    authDemoEnabled: def(parseBool(import.meta.env.VITE_AUTH_DEMO_ENABLED), false),
    betaUserID: 'beta_user',
  },
  authProviders: {
    diia: def(parseBool(import.meta.env.VITE_AUTH_PROVIDER_DIIA), false),
    idGov: def(parseBool(import.meta.env.VITE_AUTH_PROVIDER_ID_GOV), true),
    basic: def(parseBool(import.meta.env.VITE_AUTH_PROVIDER_BASIC), false),
  },
  signUp: {
    testFlow: def(
      parseBool(import.meta.env.VITE_TEST_DATA_SIGN_DEV) && localStorage.hasOwnProperty('ignore-sign'),
      false,
    ),
    downloadSignFile: def(
      parseBool(import.meta.env.VITE_TEST_DOWNLOAD_SIGN_FILE) && localStorage.hasOwnProperty('download-sign'),
      false,
    ),
  },
  cache: {
    validFrom: getDateWithoutTime(def(import.meta.env.VITE_CACHE_VALID_FROM_DATE, new Date())),
    validityPeriod: def(Number(import.meta.env.VITE_CACHE_VALIDITY_PERIOD), 1),
  },
  support: {
    email: 'services@infotech.gov.ua',
    mvsNumber: '1536',
  },
  captcha: {
    siteKey: import.meta.env.VITE_CAPTCHA_SITEKEY,
  },
  googleTagManager: {
    disabled: import.meta.env.VITE_GA_TRACK_ON === 'false',
    analystLayer: def(import.meta.env.VITE_GOOGLE_ANALYST_TAG, ''),
  },
  betaTesting: { ignoreRedirect: '?ignore=redirect' },
  router: {
    servicePath: 'services',
    userPath: 'user',
    personalRecordPath: 'personal-record',
    gercPath: 'https://www.gerc.ua/ua/feedback/',
  },
  sentry: {
    enabled: !isEmpty(import.meta.env.VITE_SENTRY_DSN),
    dsn: import.meta.env.VITE_SENTRY_DSN,

    release: versioning.VERSION,
    environment: import.meta.env.VITE_SENTRY_ENV || import.meta.env.NODE_ENV,

    // Set tracesSampleRate to 1.0 to capture 100%
    // of transactions for tracing.
    tracesSampleRate: 1.0,

    // Capture Replay for 10% of all sessions,
    // plus for 100% of sessions with an error
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,

    ignoreErrors: [
      '403 Forbidden', // Ignores errors with this specific message
      /Forbidden/, // Ignores errors matching this regex
      ...[RefreshTokenUpdateError].map((cls) => new RegExp(`.*${cls.msg}.*`, 'g')),
    ],
  },
  arcgis: {
    baseMapLayer: def(import.meta.env.VITE_ARCGIS_BASE_MAP_LAYER, ''),
    featureLayers: {
      sportCenters: def(import.meta.env.VITE_ARCGIS_SPORT_CENTERS_LAYER, ''),
    },
  },
};
